// CORE14 (docs/build-briefs/CORE14-edit-recipe.md): the model behind the recipe rows on Settings > Recipes. A drink's
// ingredients are edited as drafts; each non-pour ingredient is one of the location's own items, picked by search, never
// typed. An ingredient the location typed before this existed has no item and reads "Needs a product" until one is chosen.
// Pure functions only, no DOM, so the rules are unit tested without a browser. The server repeats every rule.

import {
  RECIPE_UNITS,
  DEFAULT_RECIPE_UNIT_VALUE,
  isRecipeUnit,
} from "../../src/domain/recipeUnits.ts";
import { formatRecipeQuantity } from "./catalogDisplay.ts";

export interface RecipeItem {
  id: string;
  name: string;
}

export interface RecipeDraft {
  /** The stored name of the ingredient; for a picked item, the item's name. */
  componentReference: string;
  /** What the quantity box shows. */
  quantity: string;
  /** The stored quantity this row was loaded with, kept so a row left alone is not rounded to two places. */
  storedQuantity: string | null;
  unit: string;
  isLiquorSlot: boolean;
  liquorProductId: string | null;
  /** True for an old typed ingredient loaded from the drink; the server keeps it unchanged until it is mapped. */
  legacy: boolean;
}

export interface StoredComponent {
  component_reference: string;
  quantity: string;
  unit: string;
  is_liquor_slot: boolean;
  liquor_product_id?: string | null;
}

export interface RecipeComponentBody {
  componentReference: string;
  quantity: number;
  unit: string;
  isLiquorSlot?: boolean;
  liquorProductId?: string;
}

export const DEFAULT_RECIPE_UNIT: string = DEFAULT_RECIPE_UNIT_VALUE;

/** The units a row's picker offers: the fixed list, plus the row's own held unit when an old recipe has one off the list. */
export function recipeUnitChoices(current: string): string[] {
  const choices: string[] = [...RECIPE_UNITS];
  if (current.length > 0 && !isRecipeUnit(current)) choices.push(current);
  return choices;
}
export const ITEM_RESULT_LIMIT = Number.POSITIVE_INFINITY;

export function blankDraft(): RecipeDraft {
  return {
    componentReference: "",
    quantity: "",
    storedQuantity: null,
    unit: DEFAULT_RECIPE_UNIT,
    isLiquorSlot: false,
    liquorProductId: null,
    legacy: false,
  };
}

export function draftsFromComponents(components: readonly StoredComponent[]): RecipeDraft[] {
  return components.map((component) => {
    const item = component.liquor_product_id ?? null;
    return {
      componentReference: component.component_reference,
      quantity: formatRecipeQuantity(component.quantity),
      storedQuantity: component.quantity,
      unit: component.unit,
      isLiquorSlot: component.is_liquor_slot,
      liquorProductId: item,
      legacy: item === null && !component.is_liquor_slot,
    };
  });
}

/** An ingredient that still has no item (and is not the liquor pour). */
export function needsProduct(draft: RecipeDraft): boolean {
  return !draft.isLiquorSlot && draft.liquorProductId === null;
}

export function chooseItem(draft: RecipeDraft, item: RecipeItem): void {
  draft.liquorProductId = item.id;
  draft.componentReference = item.name;
  draft.legacy = false;
}

/** Marks the row as the liquor pour: it takes no item, and is named for what it is. */
export function markLiquorPour(draft: RecipeDraft): void {
  draft.isLiquorSlot = true;
  draft.liquorProductId = null;
  draft.legacy = false;
  draft.componentReference = "Liquor pour";
}

export function unmarkLiquorPour(draft: RecipeDraft): void {
  draft.isLiquorSlot = false;
  draft.componentReference = "";
}

/** Items whose name contains every word typed, in any order, A to Z, at most `limit`. Blank text lists every active item. */
export function matchItems(
  items: readonly RecipeItem[],
  query: string,
  limit: number = ITEM_RESULT_LIMIT,
): RecipeItem[] {
  const words = query
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  return items
    .filter((item) => {
      const name = item.name.toLowerCase();
      return words.every((word) => name.includes(word));
    })
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, limit);
}

function quantityValue(draft: RecipeDraft): number {
  if (
    draft.storedQuantity !== null &&
    draft.quantity.trim() === formatRecipeQuantity(draft.storedQuantity)
  ) {
    return Number(draft.storedQuantity);
  }
  return Number(draft.quantity);
}

export type RecipeBodyResult =
  { ok: true; components: RecipeComponentBody[] } | { ok: false; message: string };

/** The request body for a recipe, or the sentence to show. Mirrors the server's rules. */
export function recipeToBody(
  drafts: readonly RecipeDraft[],
  options: { requiresLiquorSelection: boolean },
): RecipeBodyResult {
  const components: RecipeComponentBody[] = [];
  const seen = new Set<string>();
  for (const draft of drafts) {
    const reference = draft.componentReference.trim();
    const unit = draft.unit.trim();
    if (!draft.isLiquorSlot && draft.liquorProductId === null && !draft.legacy) {
      return { ok: false, message: "Choose an item for every ingredient." };
    }
    const quantity = quantityValue(draft);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      return {
        ok: false,
        message: `Enter an amount above 0 for ${reference.length > 0 ? reference : "every ingredient"}.`,
      };
    }
    if (unit.length === 0) {
      return {
        ok: false,
        message: `Enter a unit for ${reference.length > 0 ? reference : "every ingredient"}.`,
      };
    }
    if (reference.length === 0) {
      return { ok: false, message: "Choose an item for every ingredient." };
    }
    const key = reference.toLowerCase();
    if (seen.has(key)) {
      return { ok: false, message: `${reference} is listed twice. Combine it into one line.` };
    }
    seen.add(key);
    const body: RecipeComponentBody = { componentReference: reference, quantity, unit };
    if (draft.isLiquorSlot) body.isLiquorSlot = true;
    if (draft.liquorProductId !== null) body.liquorProductId = draft.liquorProductId;
    components.push(body);
  }
  const pours = components.filter((component) => component.isLiquorSlot === true).length;
  if (options.requiresLiquorSelection && pours !== 1) {
    return { ok: false, message: "Mark exactly one ingredient as the liquor pour." };
  }
  if (!options.requiresLiquorSelection && pours > 0) {
    return {
      ok: false,
      message: "This drink does not take a liquor selection, so no ingredient is the liquor pour.",
    };
  }
  return { ok: true, components };
}

/** True when the drafts differ from the stored recipe they were loaded from, so Save sends a recipe only when it changed. */
export function recipeChanged(
  stored: readonly StoredComponent[],
  drafts: readonly RecipeDraft[],
): boolean {
  const shape = (rows: readonly RecipeDraft[]): string =>
    JSON.stringify(
      rows.map((draft) => [
        draft.componentReference.trim(),
        quantityValue(draft),
        draft.unit.trim(),
        draft.isLiquorSlot,
        draft.liquorProductId,
      ]),
    );
  return shape(draftsFromComponents(stored)) !== shape(drafts);
}
